import { FormInstance, Spin } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AuthType,
  IntegrationFormData,
  IntegrationProtocol,
} from "../../../api/models";
import { useIntegrationStateContext } from "../context/IntegrationStateContext";
import { EmailForm } from "./EmailForm";
import { HttpForm } from "./HttpForm";
import { useFeatureFlag } from "../../../contexts/featureFlagContext";

interface IntegrationPanelFormProps {
  form: FormInstance<IntegrationFormData>;
}

export function IntegrationPanelForm({ form }: IntegrationPanelFormProps) {
  const { t } = useTranslation();
  const [protocol, setProtocol] = useState<IntegrationProtocol | "">();

  const {
    integration,
    integrationTemplate,
    integrationState,
    integrationTemplateState,
    updateIntegrationState,
    setActiveCell,
  } = useIntegrationStateContext();

  useEffect(() => {
    setProtocol(integration?.value?.protocol || "");
  }, [integration.value]);

  // Feature flag to control visibility of email protocol. Flag set by file public/feature-flags.json.
  const showEmailProtocol = useFeatureFlag("showEmailProtocol");

  const protocolChanged = (newProtocol: IntegrationProtocol) => {
    setProtocol(newProtocol);

    //Need to update active cell so that context hooks fetch correct data
    setActiveCell((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        data: {
          ...prev.data,
          protocol: newProtocol,
        },
      };
    });
  };

  if (protocol === IntegrationProtocol.HTTP) {
    return (
      <HttpForm
        updatedProtocol={protocol}
        form={form}
        formData={{
          endpointUrl: integration.value?.endpoint || "",
          integrationName: integration.value?.name || "",
          integrationProtocol: protocol || IntegrationProtocol.HTTP,
          httpMethod: integration.value?.method || "",
          authType: integration.value?.auth?.authType || AuthType.Basic,
          authValue: integration.value?.auth?.authValue || "",
          authEncoding: integration.value?.auth?.encoding,
          events: integration.value?.events || [],
          templateContent: integrationTemplate.value?.content || "",
          templateContentType:
            integrationTemplate.value?.contentType || undefined,
        }}
        integrationDataLoading={
          updateIntegrationState.loading ||
          integrationState.loading ||
          integrationTemplateState.loading
        }
        setProtocolChanged={protocolChanged}
      />
    );
    // Hide email protocol until supported, controlled by feature flag
  } else if (
    protocol === IntegrationProtocol.EMAIL &&
    showEmailProtocol.enabled
  ) {
    return (
      <EmailForm
        updatedProtocol={protocol}
        form={form}
        formData={{
          endpointUrl: integration.value?.endpoint || "",
          integrationName: integration.value?.name || "",
          integrationProtocol: protocol || IntegrationProtocol.HTTP,
          events: integration.value?.events || [],
        }}
        integrationDataLoading={integrationState.loading}
        setProtocolChanged={protocolChanged}
      />
    );
  } else {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
        }}
      >
        <Spin size="large" spinning={integrationState.loading}>
          {t("integrations:select-integration")}
        </Spin>
      </div>
    );
  }
}
